import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import type { Asset, Scene } from '@lumen5/luminary';
import { TextAlignment, VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { NarrativeThreadEnum, RecommendationDefaults } from '../../client';
import { positionAsset } from '../positioningUtils';
import { findWidowLineIndices, getRenderedTextLines } from '../video-state/renderedTextLines';
import type { ToolExecutionContext } from '../types';
import {
    ASSET_TYPES,
    describeUnavailableTemplateFont,
    getTemplateFontURL,
    getTextAndFormattingFromMarkup,
    resolveTemplateFontFamily,
} from '../video-state';

import { isNonRecommendable } from '../video-state/serialization/assetSerializer';

import {
    applyResolvedAssetTimingOperation,
    hasAssetTimingArgs,
    planAssetTiming,
    resolveAssetTimingOperation,
    withTimingNote,
} from './asset-timing-placement';
import { ASSET_TIMING_SCHEMA_SHAPE } from './asset-timing-schema';
import { nonEditableAssetError } from './non-editable-asset';
import { resolveSceneHandle } from './scene-addressing';
import type { ToolConstructorArgs } from './Tool';
import { applyMaxHeight, suspendTextAutoResize } from './text-box-height';
import { VideoStateTool } from './VideoStateTool';

import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';
import { splitFormattingAcrossSubscenes } from 'js/src/libs/scene-utils';
import { saveRawLuminary } from 'js/src/luminary/index';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { calculateAndSetRecommendedSceneDurationOperation } from 'js/src/luminary/luminary-operations';
import { resizeTextToFitOnScene } from 'js/src/luminary/luminary-utils';
import { setTextAssetSizeOperation } from 'js/src/luminary/text-editing-operations';

const FANCY_CAPTION_NAME_PATTERN = /^fancyCaption_\d+$/;

function fancyCaptionRejection(asset: Asset | undefined, assetName: string): string | null {
    const isFancyCaption = asset
        ? VideoData.isFancyCaptionAsset(asset)
        : FANCY_CAPTION_NAME_PATTERN.test(assetName);
    if (!isFancyCaption) {
        return null;
    }
    return `Error: ${assetName} is an animated caption — its wording comes from the voiceover transcript and can't be set with ${TOOL_NAMES.setText}. Use ${TOOL_NAMES.setTextColor} for its colours, or edit the voiceover to change the words.`;
}

export const setTextSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe(`The ${ASSET_TYPES.TEXT} asset name (e.g. text_1)`),
    subscene_index: z
        .number()
        .min(0)
        .optional()
        .describe(
            'The subscene index (optional, defaults to 0). Use this to target specific subscenes when a scene has multiple subscenes.',
        ),
    text: z
        .string()
        .optional()
        .describe(
            'The text content to set. Use **text** to highlight portions of text (e.g., "Welcome to **Lumen5**"). You can also bold, italicize, or underline spans with <b>…</b>, <i>…</i>, and <u>…</u> tags (e.g., "Save <b>hours</b> every week"). Prefer highlight for emphasis; only apply bold/italic/underline when the design or user calls for it — don\'t format reflexively.',
        ),
    font_size: z.number().optional().describe('The font size to set'),
    font_family: z
        .string()
        .optional()
        .describe(
            "The font family to set. Only the template's fonts are available — when the user asks for a font the template doesn't have, use the closest available template font.",
        ),
    position: z
        .object({
            x: z.number().describe('X position'),
            y: z.number().describe('Y position'),
        })
        .optional()
        .describe(
            'The x,y coordinates of where the text box is located on the canvas. Use this to MOVE the text box to a specific location. Do NOT use this for text alignment/justification.',
        ),
    width: z.number().min(1).optional().describe('The maximum width of the text asset in pixels'),
    height: z.number().min(1).optional().describe('The maximum height of the text asset in pixels'),
    alignment: z
        .enum(TextAlignment)
        .optional()
        .describe(
            'Text justification within the text box - how text lines up inside (left, center, right). Use this when users want to center-align, left-align, or right-align text.',
        ),
    ...ASSET_TIMING_SCHEMA_SHAPE,
});
export type SetTextArgs = z.infer<typeof setTextSchema>;

const EMOJI_SOURCE =
    '(?![©®™])\\p{Extended_Pictographic}(?:\\uFE0F|\\p{Emoji_Modifier}|\\u200D\\p{Extended_Pictographic})*\\uFE0F?';
const EMOJI_WITH_SURROUNDING_SPACE = new RegExp(`[ \\t]*(?:${EMOJI_SOURCE}[ \\t]*)+`, 'gu');
const CHARS_NEEDING_NO_SPACE_AFTER = new Set(['\n', '(']);
const CHARS_NEEDING_NO_SPACE_BEFORE = new Set(['\n', '.', ',', '!', '?', ';', ':', ')']);

/**
 * Emoji don't render in videos. Drops them along with the spacing around them, keeping one space
 * only where the emoji sat between two words.
 */
const stripEmoji = (text: string): { text: string; removed: string[] } => {
    const removed: string[] = [];
    const stripped = text.replace(EMOJI_WITH_SURROUNDING_SPACE, (match: string, offset: number) => {
        removed.push(...(match.match(new RegExp(EMOJI_SOURCE, 'gu')) ?? []));
        const before = text[offset - 1];
        const after = text[offset + match.length];
        // Just inside a **highlight**, the marker hugs the words it wraps.
        const insideHighlight = (text.slice(0, offset).split('**').length - 1) % 2 === 1;
        const joinsTwoWords =
            before !== undefined &&
            after !== undefined &&
            !CHARS_NEEDING_NO_SPACE_AFTER.has(before) &&
            !CHARS_NEEDING_NO_SPACE_BEFORE.has(after) &&
            !(insideHighlight && (before === '*' || after === '*'));
        return joinsTwoWords ? ' ' : '';
    });
    return { text: stripped, removed };
};

const formatNotes = (notes: readonly string[]): string =>
    notes.length > 0 ? ` Note: ${notes.join('; ')}.` : '';

const haveSameHighlights = (
    current: readonly (readonly number[])[] | undefined,
    requested: [number, number][],
): boolean => JSON.stringify(current ?? []) === JSON.stringify(requested);

/**
 * The value stored at `path`, or undefined when it is a template reference. The attribute getters
 * resolve `themeRef_*` to the slot's current value, so a reference that happens to match must
 * still count as a change: writing the literal pins it against later template changes.
 */
const storedLiteral = (asset: object, path: string): unknown => {
    const stored = VideoData.getTemplateProperty(asset, path);
    return VideoData.isTemplateReference(stored) ? undefined : stored;
};

export interface SetTextConstructorArgs extends ToolConstructorArgs {
    narrativeThread?: NarrativeThreadEnum;
}

export class SetTextTool extends VideoStateTool<typeof setTextSchema, SetTextConstructorArgs> {
    protected override readonly schema = setTextSchema;
    protected override readonly toolName = TOOL_NAMES.setText;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription = `Set text properties an asset of type ${ASSET_TYPES.TEXT} on a given scene. Do NOT use this tool on ${ASSET_TYPES.CAPTION} or ${ASSET_TYPES.FANCY_CAPTION} assets. Address the scene by its stable scene_id (shown in the video state).`;

    private narrativeThread: NarrativeThreadEnum;

    public constructor(args: SetTextConstructorArgs) {
        const { narrativeThread, ...baseArgs } = args;
        super(baseArgs as ToolConstructorArgs);
        this.narrativeThread = narrativeThread ?? NarrativeThreadEnum.TEXT;
    }

    public override getToolDisplayName(): string {
        return 'Setting text';
    }

    // The resolved global index of the scene edited by the last successful execute(), used by
    // onEditApplied — the raw args may address the scene by scene_id rather than scene_index.
    private lastEditedSceneIndex: number | undefined;

    protected override onEditApplied(): void {
        // Editing text on the first scene changes the thumbnail.
        if (this.lastEditedSceneIndex === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'title';
    }

    public async execute(args: SetTextArgs, context: ToolExecutionContext) {
        const {
            asset_name,
            subscene_index = 0,
            text: rawText,
            font_size,
            font_family,
            alignment,
            position,
            width,
            height: requestedHeight,
        } = args;

        // LLMs sometimes emit a literal backslash-n sequence instead of a real newline
        const unescapedText = rawText?.replaceAll('\\n', '\n');

        // Secondary problems are noted in the result instead of failing the whole call, so the
        // agent doesn't spend a turn re-sending every edit over one it can't make.
        const notes: string[] = [];

        // Emoji don't render in videos (they vanish or show as tofu), so they're dropped here.
        const { text, removed: removedEmoji } =
            unescapedText === undefined
                ? { text: undefined, removed: [] }
                : stripEmoji(unescapedText);
        if (removedEmoji.length > 0) {
            if (text?.trim() === '') {
                return `Error: the text is only emoji (${removedEmoji.join(' ')}), which don't render in videos. Use words instead.`;
            }
            notes.push(`emoji removed (${removedEmoji.join(' ')}), they don't render`);
        }

        try {
            // Business logic validation (schema validation already done)
            if (
                text === undefined &&
                font_size === undefined &&
                !font_family &&
                !alignment &&
                !position &&
                width === undefined &&
                requestedHeight === undefined &&
                !hasAssetTimingArgs(args)
            ) {
                return 'Error: At least one of text, font_size, font_family, alignment, position, width, height, start_words or start_time_ms must be provided';
            }

            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            this.lastEditedSceneIndex = scene_index;

            const scene = this.luminary.scenes[scene_index];
            if (!scene) {
                return `Error: No scene found for scene_id ${sceneShortId}`;
            }

            // Check if text contains subscene token (||) - requires special handling
            if (text !== undefined && text.includes(RecommendationDefaults.SUBSCENE_TOKEN)) {
                return this.handleSubsceneCreation(
                    scene,
                    scene_index,
                    sceneShortId,
                    asset_name,
                    text,
                    notes,
                    context,
                );
            }

            // Check if subscene_index is valid for this scene
            if (subscene_index >= scene.subscenes.length) {
                return `Error: subscene_index ${subscene_index} is out of bounds for scene ${sceneShortId} (has ${scene.subscenes.length} subscenes)`;
            }

            // Get asset from the appropriate subscene or scene level
            let asset;
            if (scene.subscenes.length > 1) {
                // Scene has subscenes - get asset from specific subscene
                const subscene = scene.subscenes[subscene_index];
                asset = subscene.getAssetByName(asset_name);
                if (!asset) {
                    return (
                        fancyCaptionRejection(undefined, asset_name) ??
                        `Error: No asset found with name ${asset_name} in subscene ${subscene_index} of scene ${sceneShortId}`
                    );
                }
            } else {
                // Scene has no subscenes - get asset from scene level (ignore subscene_index)
                asset = scene.getAssetByName(asset_name);
                if (!asset) {
                    return (
                        fancyCaptionRejection(undefined, asset_name) ??
                        `Error: No asset found with name ${asset_name} in scene ${sceneShortId}`
                    );
                }
            }

            if (!VideoData.isTextAsset(asset)) {
                return (
                    fancyCaptionRejection(asset, asset_name) ??
                    `Error: Asset ${asset_name} in scene ${sceneShortId} is not a ${ASSET_TYPES.TEXT} asset`
                );
            }

            if (!asset.isEditable || isNonRecommendable(scene, asset_name, this.getState())) {
                return nonEditableAssetError(asset_name, sceneShortId);
            }

            // A grouped text's position and height belong to its group. The agent often echoes the
            // height it read from the video state alongside a text edit, so a height is dropped
            // with a note; only an explicit move is refused.
            let height = requestedHeight;
            if (asset.isPositionControlledByParent) {
                const controller = asset.getFreelyPositionedParent()?.name;
                if (position !== undefined) {
                    return controller
                        ? `Error: Asset ${asset_name} in scene ${sceneShortId} is positioned by asset group ${controller} and cannot be moved on its own. Move ${controller} with ${TOOL_NAMES.setGroupAsset} instead.`
                        : `Error: Asset ${asset_name} in scene ${sceneShortId} is positioned by its parent and cannot be moved on its own.`;
                }
                if (height !== undefined) {
                    height = undefined;
                    notes.push(`height ignored: set by ${controller ?? 'its parent'}`);
                }
            }
            const hasPropertyEdits =
                text !== undefined ||
                font_size !== undefined ||
                !!font_family ||
                !!alignment ||
                position !== undefined ||
                width !== undefined ||
                height !== undefined;
            if (!hasPropertyEdits && !hasAssetTimingArgs(args)) {
                return `Error: The height of asset ${asset_name} in scene ${sceneShortId} is set by ${asset.getFreelyPositionedParent()?.name ?? 'its parent'}. Use ${TOOL_NAMES.setGroupAsset} max_height to resize the group instead.`;
            }

            const textAsset = asset;
            const sceneId = scene.id;
            const assetId = textAsset.id;
            const { parentActionGroup } = context;

            // Resolve timing before any edit lands. A timing-only call with bad timing fails
            // outright; alongside other edits the timing is adjusted or skipped with a note.
            const { resolveAssetTiming, applyResolvedAssetTiming } = makeOperationsCallable(
                { resolveAssetTimingOperation, applyResolvedAssetTimingOperation },
                this.luminary,
                this.dispatch,
                this.getState,
            );
            const timingPlan = hasAssetTimingArgs(args)
                ? planAssetTiming(
                      resolveAssetTiming(scene, textAsset, args),
                      args,
                      hasPropertyEdits,
                  )
                : undefined;
            if (timingPlan && 'error' in timingPlan) {
                return `Error: ${timingPlan.error}`;
            }
            if (timingPlan && 'skipNote' in timingPlan) {
                notes.push(timingPlan.skipNote);
            }

            // Set up operations
            const { setTextAssetSize } = makeOperationsCallable(
                {
                    setTextAssetSizeOperation,
                },
                this.luminary,
                this.dispatch,
                () => null as unknown as RootState,
            );

            const changes: string[] = [];
            // Requested values that already match, reported so the agent can tell a no-op from
            // an edit that silently failed — otherwise it retries or re-reads the video state.
            const alreadySet: string[] = [];
            const parsedText =
                text === undefined ? undefined : getTextAndFormattingFromMarkup(text);
            const textChanged =
                parsedText !== undefined &&
                (parsedText.text !== textAsset.text ||
                    !haveSameHighlights(textAsset.textHighlights, parsedText.highlights) ||
                    !haveSameHighlights(textAsset.textBold, parsedText.bold) ||
                    !haveSameHighlights(textAsset.textItalic, parsedText.italic) ||
                    !haveSameHighlights(textAsset.textUnderline, parsedText.underline));

            try {
                if (parsedText !== undefined && !textChanged) {
                    alreadySet.push(
                        parsedText.text === ''
                            ? 'text is already empty'
                            : `text is already "${parsedText.text}"`,
                    );
                }

                if (parsedText !== undefined && textChanged) {
                    // Before the write, so copy too long for the design clips and reports rather
                    // than growing the box around it. Handed back when the turn ends.
                    suspendTextAutoResize(textAsset);
                    const { text: cleanText, highlights, bold, italic, underline } = parsedText;

                    // Check if any text asset in the scene is currently being edited
                    const isEditingText = scene
                        .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
                        .some((asset) => asset.hasTextEditingEnabled);

                    if (isEditingText) {
                        this.luminary.restoreCursorAfterEffect(() => {
                            textAsset.text = cleanText;
                        });
                    } else {
                        textAsset.text = cleanText;
                    }

                    // Always set the formatting arrays (empty when the markup carried none)
                    textAsset.textHighlights = highlights;
                    textAsset.textBold = bold;
                    textAsset.textItalic = italic;
                    textAsset.textUnderline = underline;

                    // Resize the font to fit the new text, after the new text and highlights are
                    // set. resizeTextToFitOnScene forces scene.autoUpdate on so the fit runs
                    // against up-to-date metrics — the same path the editor and recommendation
                    // flows use after a content change.
                    resizeTextToFitOnScene(scene);

                    changes.push(
                        cleanText === ''
                            ? 'text content (cleared)'
                            : `text content to ${cleanText}`,
                    );
                    if (highlights.length > 0) {
                        changes.push('highlights');
                    }
                    if (bold.length + italic.length + underline.length > 0) {
                        changes.push('formatting');
                    }
                }

                // Set font size if provided
                if (font_size !== undefined) {
                    if (typeof font_size !== 'number' || font_size <= 0) {
                        return 'Error: font_size must be a positive number';
                    }
                }
                if (
                    font_size !== undefined &&
                    Math.round(textAsset.textSize) === Math.round(font_size)
                ) {
                    alreadySet.push(`font size is already ${Math.round(font_size)}`);
                } else if (font_size !== undefined) {
                    setTextAssetSize(sceneId, assetId, font_size, true, false, parentActionGroup);
                    changes.push(`font size to ${font_size}`);
                }

                if (font_family !== undefined) {
                    if (typeof font_family !== 'string' || font_family.trim() === '') {
                        return 'Error: font_family must be a non-empty string';
                    }
                    const templateFamily = resolveTemplateFontFamily(this.luminary, font_family);
                    const font_url =
                        templateFamily === null
                            ? null
                            : getTemplateFontURL(this.luminary, templateFamily);
                    if (!font_url) {
                        return `Error: ${describeUnavailableTemplateFont(this.luminary, font_family)}`;
                    }
                    if (storedLiteral(textAsset, 'normalStyle.fontUrl') === font_url) {
                        alreadySet.push(`font family is already ${templateFamily}`);
                    } else {
                        textAsset.normalStyle.fontUrl = font_url;
                        changes.push(`font family to ${templateFamily}`);
                    }
                }

                // Set alignment if provided
                if (alignment !== undefined) {
                    if (!['left', 'center', 'right'].includes(alignment)) {
                        return 'Error: alignment must be one of "left", "center", or "right"';
                    }
                    if (textAsset.textAlign === alignment) {
                        alreadySet.push(`alignment is already ${alignment}`);
                    } else {
                        textAsset.textAlign = alignment;
                        changes.push(`alignment to ${alignment}`);
                    }
                }

                // Set position if provided
                if (position !== undefined) {
                    if (typeof position !== 'object' || position === null) {
                        return 'Error: position must be an object with x and y properties';
                    }
                    if (typeof position.x !== 'number' || typeof position.y !== 'number') {
                        return 'Error: position.x and position.y must be numbers';
                    }
                    positionAsset(textAsset, {
                        x: position.x,
                        y: position.y,
                    });
                    changes.push(`position to (${position.x}, ${position.y})`);
                }

                // Set width if provided
                if (width !== undefined) {
                    if (typeof width !== 'number' || width <= 0) {
                        return 'Error: width must be a positive number';
                    }
                    if (textAsset.maxWidth === width && textAsset.hasUserChangedMaxWidth) {
                        alreadySet.push(`width is already ${width}px`);
                    } else {
                        textAsset.maxWidth = width;
                        textAsset.hasUserChangedMaxWidth = true;
                        changes.push(`width to ${width}px`);
                    }
                }

                // Set height if provided
                if (height !== undefined) {
                    const heightChange = applyMaxHeight(textAsset, height, 'height');
                    changes.push(heightChange.change);
                }

                if (changes.length > 0) {
                    this.dispatch(
                        checkpointVideo({
                            video_data: saveRawLuminary(this.luminary),
                            systemTriggered: false,
                            parentActionGroup,
                        }),
                    );
                }

                // Changing the text changes how long it takes to read, so re-fit the
                // scene duration to the new content. The operation respects user
                // overrides and skips voice-driven scenes, so callers don't gate on it.
                if (textChanged) {
                    this.recomputeSceneDuration(sceneId, parentActionGroup);
                }

                // Timing last: the asset carries its final content and the scene its re-fitted
                // duration by the time the offsets are written and checkpointed.
                if (timingPlan && 'timing' in timingPlan) {
                    const applied = withTimingNote(
                        applyResolvedAssetTiming(
                            scene,
                            textAsset,
                            timingPlan.timing,
                            timingPlan.args,
                            parentActionGroup,
                        ),
                        timingPlan.note,
                    );
                    if ('warning' in applied) {
                        notes.push(applied.warning);
                    } else {
                        changes.push(applied.change);
                    }
                    this.dispatch(
                        checkpointVideo({
                            video_data: saveRawLuminary(this.luminary),
                            systemTriggered: false,
                            parentActionGroup,
                        }),
                    );
                }
                const noteText = formatNotes(notes);

                this.recordSceneFill(scene_index, 'text');

                const subsceneInfo =
                    scene.subscenes.length > 1 ? ` subscene ${subscene_index}` : '';
                const renderReport = this.buildRenderReport(textAsset);
                const target = `asset ${asset_name} in scene ${sceneShortId}${subsceneInfo}`;
                if (changes.length === 0 && alreadySet.length > 0) {
                    return `No change: for ${target}, ${alreadySet.join(', ')}.${noteText}${renderReport}`;
                }
                const alreadySetNote =
                    alreadySet.length > 0 ? `; unchanged: ${alreadySet.join(', ')}` : '';
                const endOfUpdate = noteText ? `.${noteText}` : '';
                return `Success: Updated ${changes.join(', ')} for ${target}${alreadySetNote}${endOfUpdate}${renderReport}`;
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                return `Error updating text properties: ${errorMessage}`;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting text: ${errorMessage}`;
        }
    }

    /**
     * Reports how the asset's text actually rendered — each line as it wraps —
     * so wrapping problems (especially widows) surface in the tool result
     * without a separate render/inspect round-trip. Empty when the asset has
     * no measured metrics (e.g. not currently rendered) or no text.
     */
    private buildRenderReport(textAsset: { text?: string }): string {
        if (!textAsset.text?.trim()) {
            return '';
        }
        const lines = getRenderedTextLines(textAsset);
        if (!lines || lines.length === 0) {
            return '';
        }
        let report = ` Rendered as ${lines.length} line(s): ${lines.map((line) => `"${line.text}"`).join(' / ')}.`;
        const widowIndices = findWidowLineIndices(lines, textAsset.text);
        if (widowIndices.length > 0) {
            report += ` WARNING: line ${widowIndices
                .map((index) => index + 1)
                .join(
                    ' and ',
                )} holds a single stranded word (widow) — adjust width/font_size or shorten the text so every line has at least two words. Load the ${SKILL_NAMES.fixTextWrapping} skill for the fix procedure.`;
        }
        return report;
    }

    /**
     * Re-fit the scene's duration to its text via the shared recommended-duration
     * operation. The operation is a no-op when the duration is user-overridden or the
     * scene is voice-driven, so it's always safe to call after a text change.
     */
    private recomputeSceneDuration(sceneId: string, parentActionGroup: string | undefined): void {
        const { calculateAndSetRecommendedSceneDuration } = makeOperationsCallable(
            {
                calculateAndSetRecommendedSceneDurationOperation,
            },
            this.luminary,
            this.dispatch,
            () => this.getState(),
        );

        calculateAndSetRecommendedSceneDuration(sceneId, false, parentActionGroup);
    }

    /**
     * Handle subscene creation when text contains || (subscene token).
     * This creates multiple subscenes from a single text string, allowing
     * text to transition while media continues playing.
     */
    private handleSubsceneCreation(
        scene: Scene,
        sceneIndex: number,
        sceneShortId: string,
        assetName: string,
        text: string,
        notes: readonly string[],
        context: ToolExecutionContext,
    ): string {
        // Only allow subscene creation for TEXT narrative thread
        if (this.narrativeThread !== NarrativeThreadEnum.TEXT) {
            return `Error: Subscene creation with || is only supported for TEXT narrative thread videos. Current narrative thread: ${this.narrativeThread}`;
        }

        // Get the text asset from the scene
        const asset = scene.getAssetByName(assetName);
        if (!asset) {
            return (
                fancyCaptionRejection(undefined, assetName) ??
                `Error: No asset found with name ${assetName} in scene ${sceneShortId}`
            );
        }

        if (!VideoData.isTextAsset(asset)) {
            return (
                fancyCaptionRejection(asset, assetName) ??
                `Error: Asset ${assetName} in scene ${sceneShortId} is not a ${ASSET_TYPES.TEXT} asset`
            );
        }

        if (!asset.isEditable || isNonRecommendable(scene, assetName, this.getState())) {
            return nonEditableAssetError(assetName, sceneShortId);
        }

        try {
            // Parse markup first to extract highlights and formatting from the full text
            const {
                text: cleanText,
                highlights,
                bold,
                italic,
                underline,
            } = getTextAndFormattingFromMarkup(text);

            // Split text by subscene token
            const chunks = cleanText
                .split(RecommendationDefaults.SUBSCENE_TOKEN)
                .map((c) => c.trim());
            const numSubscenes = chunks.length;

            if (numSubscenes < 2) {
                return 'Error: Text must contain at least one || separator to create subscenes';
            }

            const emptyChunkIndex = chunks.findIndex((chunk) => chunk === '');
            if (emptyChunkIndex !== -1) {
                return `Error: part ${emptyChunkIndex} of the || split has no text — every subscene needs words, and emoji are removed because they don't render.`;
            }

            // Calculate how many subscenes need to be created
            // scene.subscenes[0] always exists (it's the main scene)
            const currentSubsceneCount = scene.subscenes.length;
            const subscenesToCreate = numSubscenes - currentSubsceneCount;

            // Create additional subscenes if needed
            for (let i = currentSubsceneCount; i < numSubscenes; i++) {
                scene.addSubscene(i);
            }

            // Split highlights and B/I/U formatting across subscenes
            const formattingChunks = splitFormattingAcrossSubscenes(
                { highlights, bold, italic, underline },
                chunks,
            );

            // Set text on each subscene (working backwards like recommendation-utils does)
            this.luminary.batchRun(() => {
                for (let i = numSubscenes - 1; i >= 0; i--) {
                    const subscene = scene.subscenes[i];
                    const subsceneAsset = subscene.getAssetByName(assetName);

                    if (subsceneAsset && VideoData.isTextAsset(subsceneAsset)) {
                        suspendTextAutoResize(subsceneAsset);
                        subsceneAsset.text = chunks[i];
                        subsceneAsset.textHighlights = formattingChunks[i].highlights;
                        subsceneAsset.textBold = formattingChunks[i].bold;
                        subsceneAsset.textItalic = formattingChunks[i].italic;
                        subsceneAsset.textUnderline = formattingChunks[i].underline;
                    }
                }
            });

            // Resize the font to fit the new text across the scene and all its subscenes
            // (getAssetsByType is deep), after all text is set. See note in execute().
            resizeTextToFitOnScene(scene);

            // Save changes
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup: context.parentActionGroup,
                }),
            );

            this.recomputeSceneDuration(scene.id, context.parentActionGroup);

            this.recordSceneFill(sceneIndex, 'text');

            const createdInfo =
                subscenesToCreate > 0 ? ` (created ${subscenesToCreate} new subscene(s))` : '';
            const noteText = formatNotes(notes);
            return `Success: Created ${numSubscenes} subscenes for asset ${assetName} in scene ${sceneShortId}${createdInfo}. Text distributed: ${chunks.map((c, i) => `[${i}] "${c}"`).join(', ')}.${noteText}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error creating subscenes: ${errorMessage}`;
        }
    }
}
